import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL =
  process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:5000";

// ================= TOKEN =================
const getToken = (token) => token;

// =====================================================
// 💾 SAVE / UPDATE FULL WEEK
// =====================================================
export const saveOtherMedicalAvailability = createAsyncThunk(
  "availability/save",
  async ({ data, token }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${BASE_URL}/availability/alternative/`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  },
);

// =====================================================
// 📥 GET MY AVAILABILITY
// =====================================================
export const getOtherMedicalAvailability = createAsyncThunk(
  "availability/getMine",
  async ({ token }, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}/availability/alternative/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  },
);

// =====================================================
// 🔍 CHECK TODAY
// =====================================================
export const checkTodayAvailability = createAsyncThunk(
  "availability/checkToday",
  async ({ token }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${BASE_URL}/availability/alternative/check`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  },
);

// =====================================================
// 📊 GET WEEKLY OPEN SLOTS
// =====================================================
export const getWeeklyOpenSlots = createAsyncThunk(
  "availability/weeklySlots",
  async ({ token }, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${BASE_URL}/availability/alternative/open-slots/weekly`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  },
);

// =====================================================
// 🧩 UPDATE SINGLE DAY
// =====================================================
export const updateSingleDay = createAsyncThunk(
  "availability/updateDay",
  async ({ data, token }, { rejectWithValue }) => {
    try {
      const res = await axios.put(
        `${BASE_URL}/availability/alternative/day`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  },
);

// =====================================================
// ⏱️ UPDATE SINGLE SLOT
// =====================================================
export const updateSingleSlot = createAsyncThunk(
  "availability/updateSlot",
  async ({ data, token }, { rejectWithValue }) => {
    try {
      const res = await axios.put(
        `${BASE_URL}/availability/alternative/slot`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  },
);

// =====================================================
// 🧹 CLEAR WEEK
// =====================================================
export const clearWeek = createAsyncThunk(
  "availability/clearWeek",
  async ({ token }, { rejectWithValue }) => {
    try {
      const res = await axios.delete(
        `${BASE_URL}/availability/alternative/clear-week`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  },
);

// =====================================================
// ❌ DELETE ALL
// =====================================================
export const deleteAvailability = createAsyncThunk(
  "availability/deleteAll",
  async ({ token }, { rejectWithValue }) => {
    try {
      const res = await axios.delete(`${BASE_URL}/availability/alternative/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  },
);

// =====================================================
// 🧠 SLICE
// =====================================================
const alternativeAvailabilitySlice = createSlice({
  name: "alternativeAvailability",
  initialState: {
    single: null, // user full schedule
    list: [], // weekly open slots
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearData: (state) => {
      state.single = null;
      state.list = [];
    },
  },
  extraReducers: (builder) => {
    builder

      // ================= SAVE =================
      .addCase(saveOtherMedicalAvailability.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveOtherMedicalAvailability.fulfilled, (state, action) => {
        state.loading = false;
        state.single = action.payload?.data;
      })
      .addCase(saveOtherMedicalAvailability.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ================= GET =================
      .addCase(getOtherMedicalAvailability.fulfilled, (state, action) => {
        state.single = action.payload?.data;
      })

      // ================= CHECK =================
      .addCase(checkTodayAvailability.fulfilled, (state, action) => {
        state.single = action.payload?.data;
      })

      // ================= WEEKLY =================
      .addCase(getWeeklyOpenSlots.fulfilled, (state, action) => {
        state.list = action.payload?.openSlots || [];
      })

      // ================= UPDATE DAY =================
      .addCase(updateSingleDay.fulfilled, (state, action) => {
        state.single = action.payload?.data;
      })

      // ================= UPDATE SLOT =================
      .addCase(updateSingleSlot.fulfilled, (state, action) => {
        state.single = action.payload?.data;
      })

      // ================= CLEAR WEEK =================
      .addCase(clearWeek.fulfilled, (state) => {
        if (state.single?.weeklyAvailability) {
          state.single.weeklyAvailability = [];
        }
      })

      // ================= DELETE ALL =================
      .addCase(deleteAvailability.fulfilled, (state) => {
        state.single = null;
        state.list = [];
      });
  },
});

export const { clearError, clearData } = alternativeAvailabilitySlice.actions;

export default alternativeAvailabilitySlice.reducer;
